
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { TenderMatch } from "@/pages/TenderAgent";
import { Button } from "@/components/ui/button";
import { 
  CalendarDays, 
  MapPin, 
  FileText, 
  ExternalLink, 
  Users, 
  Clock, 
  HelpCircle, 
  BookmarkPlus,
  Euro,
  PanelRight
} from "lucide-react";
import { formatDate } from "@/utils/dateFormatters";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getMainDeadline, getAdditionalInfoDeadline, isProcedureOpen } from "@/utils/dateFormatters";
import { getEnglishText } from "../../../supabase/functions/shared/text-utils";
import { getProcedureTypeDescription } from "@/utils/procedureTypeUtils";

interface TenderDetailsProps {
  tender: TenderMatch | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveTender?: () => Promise<void>;
}

export const TenderDetails = ({ 
  tender, 
  isOpen, 
  onOpenChange,
  onSaveTender
}: TenderDetailsProps) => {
  const { t, i18n } = useTranslation("tenderAgent");
  const isCurrentLanguageDanish = i18n.language === "da";

  if (!tender) {
    return null;
  }

  // Get title and set a fallback
  const getTitle = () => {
    if (tender["notice-title"]) {
      return getEnglishText(tender["notice-title"], "Tender " + (tender.publication_number || tender.id || ""));
    }
    return "Tender " + (tender.publication_number || tender.id || "");
  };

  // Get organization and set a fallback
  const getOrganization = () => {
    // Try buyer-name first with eng language field
    if (tender["buyer-name"]) {
      return getEnglishText(tender["buyer-name"], "Organization not specified");
    }
    
    // Then try AU field which might contain organization name in TED data
    if (tender.AU) {
      return typeof tender.AU === 'string' 
        ? tender.AU 
        : getEnglishText(tender.AU, "Organization not specified");
    }
    
    // Then try organization_name which might be set directly
    if (tender.organization_name) {
      return tender.organization_name;
    }
    
    return "Organization not specified";
  };

  // Get description and set a fallback
  const getDescription = () => {
    if (tender["description-lot"]) {
      return getEnglishText(tender["description-lot"], "No description available");
    }
    
    // Check for description in other formats/locations
    // Use type checking to avoid TypeScript errors
    if (tender.tender_data && typeof tender.tender_data === 'object' && 'description' in tender.tender_data) {
      return tender.tender_data.description;
    }
    
    // Also check direct description property with type checking
    if ('description' in tender && typeof tender.description === 'string') {
      return tender.description;
    }
    
    return "No description available";
  };

  // Get location and set a fallback
  const getLocation = () => {
    // Check buyer-country first
    if (tender["buyer-country"]) {
      return tender["buyer-country"];
    }
    
    // Then check CY field which is common in TED data
    if (tender.CY) {
      return tender.CY;
    }
    
    // Then check place-of-performance
    if (tender["place-of-performance"]) {
      return tender["place-of-performance"];
    }
    
    // Then check RC field
    if (tender.RC) {
      return tender.RC;
    }
    
    // Check place-of-performance-country-lot if it's an array
    if (Array.isArray(tender["place-of-performance-country-lot"]) && tender["place-of-performance-country-lot"].length > 0) {
      return tender["place-of-performance-country-lot"][0];
    }
    
    return isCurrentLanguageDanish ? "Lokation ikke angivet" : "Location not specified";
  };
  
  // Get delivery area (NUTS)
  const getDeliveryArea = () => {
    // Check buyer-country-sub first
    if (tender["buyer-country-sub"]) {
      return tender["buyer-country-sub"];
    }
    
    // Then check BT-5071-Lot field
    if (tender["BT-5071-Lot"]) {
      return tender["BT-5071-Lot"];
    }
    
    // Check place-of-performance-subdiv-lot if it's an array
    if (Array.isArray(tender["place-of-performance-subdiv-lot"]) && tender["place-of-performance-subdiv-lot"].length > 0) {
      return tender["place-of-performance-subdiv-lot"].join(", ");
    }
    
    return null;
  };

  // Get CPV codes
  const getCPVCodes = () => {
    if (Array.isArray(tender["classification-cpv"]) && tender["classification-cpv"].length > 0) {
      return tender["classification-cpv"];
    }
    return null;
  };

  // Get procedure type - improved to check more fields with proper null checks
  const getProcedureType = () => {
    let procedureTypeCode = null;
    
    // Check all possible fields where procedure type might be stored with null checks
    if (tender && tender["procedure-type"]) {
      procedureTypeCode = tender["procedure-type"];
    } else if (tender && tender.PR) {
      procedureTypeCode = tender.PR;
    } else if (tender && tender.tender_data && tender.tender_data["procedure-type"]) {
      procedureTypeCode = tender.tender_data["procedure-type"];
    } else if (tender && tender.TOP_TYPE) {
      procedureTypeCode = tender.TOP_TYPE;
    } else if (tender && tender.tenderType) {
      procedureTypeCode = tender.tenderType;
    } else if (tender && tender["BT-105-Procedure"]) {
      procedureTypeCode = tender["BT-105-Procedure"];
    }
    
    return procedureTypeCode ? getProcedureTypeDescription(procedureTypeCode) : (isCurrentLanguageDanish ? "Proceduretype ikke angivet" : "Procedure type not specified");
  };

  // Get value
  const getValue = () => {
    if (tender["estimated-value-proc"]) {
      const value = tender["estimated-value-proc"];
      const currency = tender["estimated-value-cur-proc"] || "EUR";
      return `${value} ${currency}`;
    }
    if (tender["tender-value"]?.value) {
      return `${tender["tender-value"].value} ${tender["tender-value"].currency || "EUR"}`;
    }
    if (tender.value) {
      return tender.value;
    }
    return isCurrentLanguageDanish ? "Værdi ikke angivet" : "Value not specified";
  };

  // Get submission URL
  const getSubmissionUrl = () => {
    if (tender.submission_url) {
      return tender.submission_url;
    }
    if (Array.isArray(tender["submission-url-lot"]) && tender["submission-url-lot"].length > 0) {
      return tender["submission-url-lot"][0];
    }
    return null;
  };

  // Use the main deadline (DT) and show label based on procedure type
  const mainDeadlineStr = getMainDeadline(tender);
  const mainDeadline = mainDeadlineStr ? formatDate(mainDeadlineStr, isCurrentLanguageDanish) : (isCurrentLanguageDanish ? "Frist ikke angivet" : "Deadline not specified");
  
  // Use the additional information request deadline (deadline)
  const additionalInfoDeadlineStr = getAdditionalInfoDeadline(tender);
  const additionalInfoDeadline = additionalInfoDeadlineStr ? formatDate(additionalInfoDeadlineStr, isCurrentLanguageDanish) : null;
  
  // Determine if this is an open or restricted procedure
  const isProcedureOpenType = isProcedureOpen(tender);
  
  // Set deadline label based on procedure type
  const mainDeadlineLabel = isProcedureOpenType 
    ? (isCurrentLanguageDanish ? "Tilbudsfrist" : "Bid deadline") 
    : (isCurrentLanguageDanish ? "Ansøgningsfrist" : "Application deadline");

  // Ensure we have a valid identifier for linking to the tender detail page
  const tenderId = tender.publication_number || tender.id || "";
  
  // Get location and delivery area information
  const location = getLocation();
  const deliveryArea = getDeliveryArea();
  const procedureType = getProcedureType();

  const handleSaveTender = async () => {
    if (onSaveTender) {
      onSaveTender();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-medium">{getTitle()}</h2>
            <p className="text-sm text-gray-500">{getOrganization()}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-2">
              <div className="text-primary mt-0.5">
                <Clock className="h-4 w-4" />
              </div>
              <div>
                <span className="text-sm font-medium block">{mainDeadlineLabel}</span>
                <span className="text-sm text-gray-600">{mainDeadline}</span>
              </div>
            </div>

            {additionalInfoDeadline && (
              <div className="flex items-start space-x-2">
                <div className="text-primary mt-0.5">
                  <HelpCircle className="h-4 w-4" />
                </div>
                <div>
                  <span className="text-sm font-medium block">
                    {isCurrentLanguageDanish ? "Frist for supplerende oplysninger" : "Deadline for additional information"}
                  </span>
                  <span className="text-sm text-gray-600">{additionalInfoDeadline}</span>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-2">
              <div className="text-primary mt-0.5">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <span className="text-sm font-medium block">{t("tenderDetails.location")}</span>
                <div className="text-sm text-gray-600">
                  <div>{location}</div>
                  {deliveryArea && <div className="mt-1 italic text-xs">{isCurrentLanguageDanish ? "Leveringssted: " : "Delivery area: "}{deliveryArea}</div>}
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <div className="text-primary mt-0.5">
                <FileText className="h-4 w-4" />
              </div>
              <div>
                <span className="text-sm font-medium block">{t("tenderDetails.publicationNumber")}</span>
                <span className="text-sm text-gray-600">
                  {tender.publication_number || "Not specified"}
                </span>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <div className="text-primary mt-0.5">
                <PanelRight className="h-4 w-4" />
              </div>
              <div>
                <span className="text-sm font-medium block">{isCurrentLanguageDanish ? "Proceduretype" : "Procedure type"}</span>
                <span className="text-sm text-gray-600">{procedureType}</span>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <div className="text-primary mt-0.5">
                <Euro className="h-4 w-4" />
              </div>
              <div>
                <span className="text-sm font-medium block">{isCurrentLanguageDanish ? "Værdi" : "Value"}</span>
                <span className="text-sm text-gray-600">{getValue()}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2">{t("tenderDetails.description")}</h3>
            <p className="text-sm text-gray-600 max-h-60 overflow-y-auto border border-gray-100 rounded-md p-3 bg-gray-50">
              {getDescription()}
            </p>
          </div>

          {getCPVCodes() && (
            <div>
              <h3 className="text-sm font-medium mb-2">{t("tenderDetails.cpvCodes")}</h3>
              <div className="flex flex-wrap gap-2">
                {getCPVCodes()?.map((code, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {code}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 justify-end">
            {getSubmissionUrl() && (
              <Button
                variant="outline"
                className="flex items-center gap-2"
                asChild
              >
                <a href={getSubmissionUrl()} target="_blank" rel="noopener noreferrer">
                  {t("tenderDetails.contractDocuments")}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            )}

            {onSaveTender && (
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => handleSaveTender()}
              >
                <BookmarkPlus className="h-4 w-4" />
                {isCurrentLanguageDanish ? "Gem" : "Save"}
              </Button>
            )}

            <Button asChild>
              <Link to={`/tender-view/${tenderId}`}>
                {t("tenderDetails.viewDetails")}
              </Link>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
